import { useCallback, useEffect, useState } from 'react';
import type { FinanceSummary } from '../types/finance';
import { getFinanceSummary, type FinanceSummaryQuery } from '../lib/api/endpoints';
import { financeSummaryFromDto } from '../lib/api/mappers';

/**
 * FE-32 — el resultado del periodo, leído de los agregados del servidor
 * (ADR-016). Aquí NO se suma nada: los totales que se pintan son los que el
 * motor calculó, y por eso cuadran con el XLSX que el mismo motor exporta.
 */
export function useFinanceSummary(query: FinanceSummaryQuery | null) {
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [isLoading, setIsLoading] = useState(!!query);
  const [error, setError] = useState<Error | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const queryKey = JSON.stringify(query);

  useEffect(() => {
    const parsed = JSON.parse(queryKey) as FinanceSummaryQuery | null;
    if (!parsed) {
      setSummary(null);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const res = await getFinanceSummary(parsed);
        if (cancelled) return;
        setSummary(financeSummaryFromDto(res));
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setSummary(null);
          setError(err instanceof Error ? err : new Error('Error desconocido cargando el resumen'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [queryKey, reloadToken]);

  const reload = useCallback(() => setReloadToken(t => t + 1), []);
  return { summary, isLoading, error, reload };
}
